/* global Office */

/**
 * Detects the current Outlook context and returns information about where the user is
 */
export const detectOutlookContext = () => {
  try {
    const mailbox = Office?.context?.mailbox;
    const item = mailbox?.item;

    console.log('🔍 Context Detection Debug:');
    console.log('  - Mailbox exists:', !!mailbox);
    console.log('  - Item exists:', !!item);
    console.log('  - Item type:', item?.itemType);

    // If no mailbox, we're in an unsupported context
    if (!mailbox) {
      console.log('  ❌ No mailbox - UNSUPPORTED context');
      return {
        type: 'UNSUPPORTED',
        description: 'Mailbox context not available',
        hasItem: false,
      };
    }

    // If no item, we're in the module view (inbox/folder view without a selected message)
    if (!item) {
      console.log('  📂 No item - MODULE context (Inbox view)');
      return {
        type: 'MODULE',
        description: 'Main mailbox view (Posteingang, Entwürfe, etc.)',
        hasItem: false,
      };
    }

    // We have an item, determine if it's read or compose mode
    const itemType = item.itemType; // "message" or "appointment"

    if (itemType === 'message') {
      // Reliable way to detect compose mode without relying on Office.MailboxEnums
      // Compose mode has async methods for properties, Read mode has direct values
      const hasSubjectGetAsync = typeof item.subject?.getAsync === 'function';
      const hasToAddAsync = typeof item.to?.addAsync === 'function';
      const hasDirectItemId = typeof item.itemId === 'string' && item.itemId.length > 0;
      const hasGetItemIdAsync = typeof item.getItemIdAsync === 'function';
      
      // In compose mode:
      // - subject is an object with getAsync/setAsync methods
      // - to/cc/bcc have addAsync/getAsync/setAsync methods  
      // - itemId is not directly available (only via getItemIdAsync, and it's empty until sent)
      const isCompose = hasSubjectGetAsync && hasToAddAsync && !hasDirectItemId;

      console.log('  📧 Message detected - Detection signals:');
      console.log('  - has subject.getAsync:', hasSubjectGetAsync);
      console.log('  - has to.addAsync:', hasToAddAsync);
      console.log('  - has direct itemId:', hasDirectItemId);
      console.log('  - has getItemIdAsync:', hasGetItemIdAsync);
      console.log('  ➡️ isCompose:', isCompose);

      if (isCompose) {
        console.log('  ✍️ MESSAGE_COMPOSE context');
        return {
          type: 'MESSAGE_COMPOSE',
          description: 'Composing or replying to a message',
          hasItem: true,
          itemType,
        };
      } else {
        console.log('  📖 MESSAGE_READ context');
        return {
          type: 'MESSAGE_READ',
          description: 'Reading a message',
          hasItem: true,
          itemType,
          subject: item.subject,
          conversationId: item.conversationId,
          itemId: item.itemId,
        };
      }
    }

    // Default fallback
    console.log('  ❓ UNKNOWN context');
    return {
      type: 'UNKNOWN',
      description: 'Unknown context',
      hasItem: true,
      itemType,
    };
  } catch (error) {
    console.error('❌ Error detecting Outlook context:', error);
    return {
      type: 'ERROR',
      description: 'Error detecting context',
      error: error.message,
      hasItem: false,
    };
  }
};

/**
 * Checks if the current context is a message read context
 */
export const isMessageReadContext = () => {
  const context = detectOutlookContext();
  return context.type === 'MESSAGE_READ';
};

/**
 * Checks if the current context is a message compose context
 */
export const isMessageComposeContext = () => {
  const context = detectOutlookContext();
  return context.type === 'MESSAGE_COMPOSE';
};

/**
 * Checks if the current context is the module view (no message selected)
 */
export const isModuleContext = () => {
  const context = detectOutlookContext();
  return context.type === 'MODULE';
};

/**
 * Detects if the current item is in a shared mailbox or shared folder
 * Returns a Promise that resolves with shared mailbox information
 * 
 * @returns {Promise<Object>} Shared properties including:
 *   - isShared: boolean indicating if this is a shared context
 *   - owner: email address of the mailbox owner
 *   - delegatePermissions: bitmask of delegate permissions
 *   - targetMailbox: email address to use for Microsoft Graph calls
 */
export const detectSharedMailboxContext = () => {
  return new Promise((resolve, reject) => {
    try {
      const item = Office?.context?.mailbox?.item;
      
      if (!item) {
        console.log('🔍 Shared context check: No item available');
        resolve({
          isShared: false,
          reason: 'No item context available',
        });
        return;
      }

      // Check if getSharedPropertiesAsync is available (Mailbox 1.8+)
      if (typeof item.getSharedPropertiesAsync !== 'function') {
        console.log('🔍 Shared context check: getSharedPropertiesAsync not available (requires Mailbox 1.8+)');
        resolve({
          isShared: false,
          reason: 'API not available (requires Outlook API 1.8+)',
        });
        return;
      }

      console.log('🔍 Checking for shared mailbox context...');
      
      item.getSharedPropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const sharedProperties = result.value;
          
          // If owner is present, this is a shared context
          const isShared = !!sharedProperties.owner;
          
          console.log('✅ Shared context detection successful:');
          console.log('  - Is shared:', isShared);
          console.log('  - Owner:', sharedProperties.owner);
          console.log('  - Delegate permissions:', sharedProperties.delegatePermissions);
          console.log('  - Target mailbox:', sharedProperties.targetMailbox);
          
          resolve({
            isShared,
            owner: sharedProperties.owner || null,
            delegatePermissions: sharedProperties.delegatePermissions || 0,
            targetMailbox: sharedProperties.targetMailbox || sharedProperties.owner || null,
            // Decode permission flags for convenience
            permissions: {
              read: (sharedProperties.delegatePermissions & Office.MailboxEnums.DelegatePermissions.Read) !== 0,
              write: (sharedProperties.delegatePermissions & Office.MailboxEnums.DelegatePermissions.Write) !== 0,
              deleteOwn: (sharedProperties.delegatePermissions & Office.MailboxEnums.DelegatePermissions.DeleteOwn) !== 0,
              deleteAll: (sharedProperties.delegatePermissions & Office.MailboxEnums.DelegatePermissions.DeleteAll) !== 0,
            },
          });
        } else {
          console.warn('⚠️ Shared context detection failed:', result.error);
          resolve({
            isShared: false,
            error: result.error?.message || 'Unknown error',
          });
        }
      });
    } catch (error) {
      console.error('❌ Error detecting shared context:', error);
      reject(error);
    }
  });
};

/**
 * Gets the appropriate mailbox email address for Microsoft Graph API calls
 * For shared mailboxes, returns the owner's email; otherwise returns user's email
 * 
 * @returns {Promise<string>} Email address to use for Graph API calls
 */
export const getGraphMailboxAddress = async () => {
  try {
    const userProfile = Office.context.mailbox.userProfile;
    const defaultEmail = userProfile.emailAddress;
    
    const sharedContext = await detectSharedMailboxContext();
    
    if (sharedContext.isShared && sharedContext.targetMailbox) {
      console.log(`📬 Using shared mailbox address for Graph: ${sharedContext.targetMailbox}`);
      return sharedContext.targetMailbox;
    }
    
    console.log(`📬 Using user's mailbox address for Graph: ${defaultEmail}`);
    return defaultEmail;
  } catch (error) {
    console.error('❌ Error getting Graph mailbox address:', error);
    // Fallback to user's email
    return Office.context.mailbox.userProfile.emailAddress;
  }
};

